<template>
  <!-- App.vue -->
  <v-app>
    <v-app-bar
      v-if="app_ready && app_layout === 'full'"
      app
      color="primary"
      dark
      shrink-on-scroll
      height="150"
      elevation="0"
      :src="current_property ? current_property.cover_image : null"
    >
      <template v-if="current_property" v-slot:img="{ props }">
        <v-img
            v-bind="props"
            gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
        ></v-img>
      </template>
      <v-toolbar-title v-if="current_property" class="align-center" dark>
        <profile-avatar
            :profile="current_property"
            :size="40"
        />
        <span class="ml-2">{{ current_property.name }}</span>
      </v-toolbar-title>
      <v-toolbar-title v-else dark>
        <h4>{{ current_page.title || appName }}</h4>
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <header-notifications class="mr-2" />
      <app-menu @signout="signUserOut" />
    </v-app-bar>

    <!-- Sizes your content based upon application components -->
    <v-main v-if="app_ready">
      <router-view v-if="!auth_required"></router-view>
    </v-main>
    <v-main v-else>
      <div  class="d-flex justify-center align-center" style="height: 80vh">
        <div v-if="error" >
          <error-handler :error="error" @retry=" updateExists ? refreshApp : reloadApp" />
          <div v-if="updateExists" class="text-center">
            <h2>This might be due to outdated version, please update the application</h2>
          </div>
        </div>
        <div v-else class="text-center">
          <img src="@/assets/img/logo.png" width="100px" class="loader" />
          <p>{{app_process}}</p>
        </div>
      </div>
    </v-main>

    <v-snackbar
        v-if="app_layout === 'full' && mode === 'application'" top right
        :value="updateExists" :timeout="-1"
        color="primary"
    >
      <v-row justify="space-around" align="start">
        <v-col cols="9" class="my-0 py-0">
          An update is available
        </v-col>
        <v-col cols="3" class="my-0 py-0">
          <v-btn text small dark @click="refreshApp" >
            Update
          </v-btn>
        </v-col>
      </v-row>
    </v-snackbar>

    <v-footer v-if="app_layout === 'full'" app>
      <a
          v-if="auth.profile"
          class="d-flex align-center text-decoration-none" style="cursor: pointer"
          :href="authDomain"
          target="_blank"
          >
        <profile-avatar size="24" :profile="auth.profile" />
        <h6 class="ml-2">{{ auth.profile.full_name }}</h6>
      </a>
      <v-spacer></v-spacer>
      <v-btn x-small @click="updateApp" title="Reload app" icon><v-icon>mdi-refresh</v-icon></v-btn>
    </v-footer>
  </v-app>
</template>

<script>

import {mapActions, mapMutations, mapGetters} from 'vuex'
import CryptoJS from 'crypto-js';
import appHelper from '@/helper/app';
import update from './mixins/update';
import config from './config';
import ProfileAvatar from "@/components/ProfileAvatar.vue";
import ErrorHandler from "@/components/ErrorHandler.vue";
import AppMenu from "@/components/AppMenu.vue";
import HeaderNotifications from "@/components/HeaderNotifications.vue";
import session from "@/domain/Reservation/Mixins/session";
import moment from "moment";
import querystring from "querystring";
import Lc from "@/lc";

export default {
  name: 'App',
  mixins: [update, session],
  components: {
    HeaderNotifications,
    AppMenu,
    ErrorHandler,
    ProfileAvatar,
  },
  data(){
    return {
      appName: config.app.name,
      authDomain: config.app.authDomain,
      error: null,
    }
  },

  computed:{
    ...mapGetters([
      'app_ready',
      'app_process',
      'authenticated',
      'profile_loaded',
      'app_layout',
      'current_user',
      'current_property',
      'current_page',
      'is_mobile',
      'auth', 'mode',
      'auth_required',
      'auth_params',
      'checkin_session',
    ]),
  },

    methods:{

      ...mapActions([
          'syncAuthUser',
          'getSystemParams',
          'signout',
          'query',
          'mutate'
      ]),
      
      ...mapMutations([
          'SET_AUTH',
          'SET_AUTH_REQUIRED',
          'SET_APP_STATE',
          'SNACKBAR',
          'UNSET_CURRENT_USER',
          'SET_APP_LAYOUT',
          'SET_MOBILE',
          'SET_MODE',
          'SET_CURRENT_PAGE'
      ]),

      setUser(){
          this.error = null;
          this.SET_APP_STATE(!!this.auth.token && !!this.current_user.profile);
          this.syncAuthUser()
          .then(() => {
            this.SET_APP_STATE(!!this.current_user.profile)
          })
          .catch(e => {
            if(this.updateExists) {
              this.refreshApp();
            } else {
              this.error = e
            }
          })
          .finally(() => {
            this.bootIntercom();
          })
      },

      bootIntercom() {
        if(config.intercom.app_id && this.auth?.profile) {
          this.$intercom.boot({
            user_id: this.auth.profile.id,
            name: this.auth.profile.full_name,
            email: this.auth.profile.email,
            user_hash: CryptoJS.HmacSHA256(this.auth.profile.id, config.intercom.secret_key).toString(),
            hide_default_launcher: false
          })
        }
      },

      signUserOut(){
        this.SET_APP_STATE(false)
        this.signout()
        .then(() => {
          this.$intercom?.shutdown();
          Lc.signout();
        }).finally(() => {
          this.SET_APP_STATE(true);
        })
      },
    },

    mounted() {
      if(this.mode === 'sdk'){
        this.SET_APP_STATE(true);
        return
      }
      this.getSystemParams().then(() => {
        const critical = appHelper.checkForCriticalUpdate()
        if(critical) {
          if(appHelper.useServiceWorker()) {
            this.updateCritical = critical;
            this.refreshApp()
          } else this.updateApp();
          return Promise.reject(
              "Cannot start application, critical update is required. Hold on while application is rebooted"
          )
        } else if(this.$route.meta.requiresAuth) this.setUser()
        else this.SET_APP_STATE(true)
      })
      .catch(e => {
        console.log(e);
      });
    },

    created() {
        this.SET_MOBILE( screen.width < 768);
        window.addEventListener('resize', (e) => {
          this.SET_MOBILE( screen.width < 768)
        })
    },

    watch: {
      $route: {
        immediate: true,
        handler(route) {
          this.SET_CURRENT_PAGE({ title: route.meta.title });
          this.SET_MODE(
              route.query?.source === "sdk"
              ? 'sdk' : 'application'
          )
          this.SET_APP_LAYOUT(this.mode === 'sdk' ? 'plain' : route.meta.layout || 'full');
        }
      }
    }
}
</script>

<style>
  .theme--light.v-application {
    background-color: #f7f7f7 !important;
  }

  .required label::after {
    content: "*";
  }

  .loader {
    animation: fadeInfadeOut 1.5s linear 0s infinite;
  }

  @keyframes fadeInfadeOut {
    0% {
      opacity: 0.2;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.2;
    }
  }
  .text-transform-unset {
    text-transform: unset !important;
  }
  .box-shadow-unset {
    box-shadow: unset !important;
    -webkit-box-shadow: unset !important;
  }

</style>
